import { FetchableResourceStatus } from '~/_shared/types/fetchableResource/fetchableResource';
import { isResourceLoaded } from '~/_shared/types/fetchableResource/fetchableResource.helpers';
import { compareSpreadsheetColumnIds } from '~/_shared/types/spreadsheetData/spreadsheetColumns.helpers';
import { ChartMetricType } from '../mapSettings/data/charts/mapSettingsCharts.types';
import {
  type BoundaryStatsRequestItem, StatsProcessingStatus,
} from './stats.repository';
import type {
  BoundaryStatItemDemographic,
  BoundaryStatItemDemographicMetricData,
  BoundaryStatItemSpreadsheetColumn,
  BoundaryStatItemSpreadsheetColumnMetricData, BoundaryStatsItem, StatItem, StatItemMetricData,
  StatsState,
} from './stats.state';

export const isStatsItemLoading = (statsItem: StatItem): boolean => (
  statsItem.data.status === FetchableResourceStatus.Loading
  || statsItem.data.status === FetchableResourceStatus.Requested
);

export const isStatsItemLoaded = (statsItem: StatItem): boolean => (
  isResourceLoaded(statsItem.data)
);

export const isStatsItemSyncing = (statsItem: StatItem): boolean => (
  isResourceLoaded(statsItem.data)
  && statsItem.data.value.status !== StatsProcessingStatus.Finished
);

export const isBoundaryStatItem = (metricData: StatItem): metricData is BoundaryStatsItem => (
  metricData.boundaryGroupId !== undefined
);

export const isBoundaryStatItemSpreadsheetColumn = (statItem: StatItem): statItem is BoundaryStatItemSpreadsheetColumn => (
  isBoundaryStatItem(statItem) && statItem.type === ChartMetricType.SpreadsheetColumn
);

export const isBoundaryStatItemDemographic = (statItem: StatItem): statItem is BoundaryStatItemDemographic => (
  isBoundaryStatItem(statItem) && statItem.type === ChartMetricType.Demographic
);

export const isBoundaryStatItemSpreadsheetColumnMetricData = (metricData: StatItemMetricData): metricData is BoundaryStatItemSpreadsheetColumnMetricData => (
  metricData.boundaryGroupId !== undefined && metricData.type === ChartMetricType.SpreadsheetColumn
);

export const isBoundaryStatItemDemographicMetricData = (metricData: StatItemMetricData): metricData is BoundaryStatItemDemographicMetricData => (
  metricData.boundaryGroupId !== undefined && metricData.type === ChartMetricType.Demographic
);

export const findBoundaryGroupStatItemSpreadsheetColumn = (
  boundaryGroupsStats: StatsState['boundaryGroupStats'],
  metricData: BoundaryStatItemSpreadsheetColumnMetricData,
): BoundaryStatItemSpreadsheetColumn | null => {
  const boundaryGroupStats = boundaryGroupsStats.get(metricData.boundaryGroupId);
  if (boundaryGroupStats) {
    return Array.from(boundaryGroupStats.values())
      .filter(isBoundaryStatItemSpreadsheetColumn)
      .find(stat => compareSpreadsheetColumnIds(stat.column, metricData.column)) ?? null;
  }
  return null;
};

export const findBoundaryGroupStatItemDemographic = (
  boundaryGroupsStats: StatsState['boundaryGroupStats'],
  metricData: BoundaryStatItemDemographicMetricData,
): BoundaryStatItemDemographic | null => {
  const boundaryGroupStats = boundaryGroupsStats.get(metricData.boundaryGroupId);
  if (boundaryGroupStats) {
    return Array.from(boundaryGroupStats.values())
      .filter(isBoundaryStatItemDemographic)
      .find(stat => stat.demographicId === metricData.demographicId) ?? null;
  }
  return null;
};

export const boundaryStatsItemToBoundaryStatsRequestItem = (statsItem: BoundaryStatsItem): BoundaryStatsRequestItem => ({
  query_id: statsItem.id,
  boundary_group_id: statsItem.boundaryGroupId,
  ...(statsItem.type === ChartMetricType.SpreadsheetColumn ? {
    type: ChartMetricType.SpreadsheetColumn,
    spreadsheet_id: statsItem.column.spreadsheetId,
    metric: statsItem.column.columnId,
  } : {
    type: ChartMetricType.Demographic,
    metric: statsItem.demographicId,
  }),
});

export const setStatsItemToLoading = (statItem: StatItem): StatItem => {
  if (!isResourceLoaded(statItem.data)) {
    return {
      ...statItem,
      data: {
        status: FetchableResourceStatus.Loading,
      },
    };
  }

  if (isBoundaryStatItemSpreadsheetColumn(statItem)) {
    return {
      ...statItem,
      data: {
        ...statItem.data,
        value: {
          ...statItem.data.value,
          status: StatsProcessingStatus.InProgress,
        },
      },
    };
  }

  if (isBoundaryStatItemDemographic(statItem)) {
    return {
      ...statItem,
      data: {
        ...statItem.data,
        value: {
          ...statItem.data.value,
          status: StatsProcessingStatus.InProgress,
        },
      },
    };
  }
  return statItem;
};
